import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { UserButton } from "@clerk/react";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Loader2, DollarSign, TrendingUp, ShieldAlert,
  FileText, CheckCircle2, Clock, Send, XCircle, RefreshCw,
  Building2, Users, BarChart3, AlertTriangle,
} from "lucide-react";

interface PoolRow {
  id: number;
  name: string;
  balance: number;
  threshold: number;
  status: string;
  percentFunded: number;
}

interface BatchRow {
  id: number;
  totalAmount: number;
  debtsCount: number;
  status: string;
  paymentMethod: string;
  createdAt: string;
}

interface AuditLogRow {
  id: number;
  action: string;
  actorEmail: string | null;
  entityType: string;
  entityId: number | null;
  createdAt: string;
}

interface ComplianceAlertItem {
  id: number;
  alertType: string;
  severity: string;
  detectedAt: string;
  notes: string | null;
}

interface BoardOverview {
  financials: {
    totalDonations: number;
    donationCount: number;
    totalPaidOut: number;
    payoutBatchCount: number;
    netReserve: number;
  };
  pools: PoolRow[];
  compliance: {
    openAlerts: number;
    items: ComplianceAlertItem[];
  };
  debtStats: {
    total: number;
    queued: number;
    paid: number;
    pending: number;
  };
  recentBatches: BatchRow[];
  recentAuditLogs: AuditLogRow[];
  asOf: string;
}

function fmt(n: number) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtDateTime(s: string) {
  return new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const BATCH_STATUS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending_approval: { label: "Awaiting Approval", color: "bg-blue-100 text-blue-800 border-blue-200", icon: <Clock className="h-3 w-3" /> },
  pending: { label: "Processing", color: "bg-yellow-100 text-yellow-800 border-yellow-200", icon: <Clock className="h-3 w-3" /> },
  sent: { label: "Payment Sent", color: "bg-yellow-100 text-yellow-800 border-yellow-200", icon: <Send className="h-3 w-3" /> },
  confirmed: { label: "Confirmed", color: "bg-green-100 text-green-800 border-green-200", icon: <CheckCircle2 className="h-3 w-3" /> },
  failed: { label: "Failed", color: "bg-red-100 text-red-800 border-red-200", icon: <XCircle className="h-3 w-3" /> },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800 border-red-200", icon: <XCircle className="h-3 w-3" /> },
};

const POOL_STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  funded: "bg-blue-100 text-blue-800 border-blue-200",
  disbursed: "bg-purple-100 text-purple-800 border-purple-200",
  archived: "bg-gray-100 text-gray-600 border-gray-200",
};

function StatCard({ label, value, sub, icon: Icon, accent }: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  accent: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-5 flex items-start gap-4">
      <div className={`rounded-lg p-2.5 ${accent}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <p className="text-2xl font-bold tracking-tight mt-0.5">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function BoardGovernanceDashboard() {
  const [, navigate] = useLocation();

  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useQuery<BoardOverview>({
    queryKey: ["board-overview"],
    queryFn: async () => {
      const r = await fetch("/api/admin/board/overview", { credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/admin-entry")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Exit
            </Button>
            <LogoMark size={26} />
            <div>
              <span className="text-sm font-semibold text-white">Board Governance Portal</span>
              <span className="ml-2 text-xs text-slate-400 hidden sm:inline">DebtBridge 501(c)(3)</span>
            </div>
            <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
          </div>
          <div className="flex items-center gap-2">
            {isFetching && <RefreshCw className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            {data && (
              <span className="text-xs text-slate-400 hidden sm:inline">
                As of {fmtDateTime(data.asOf)}
              </span>
            )}
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => void refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <UserButton />
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Board Member Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Read-only overview of financial health, program activity, and governance metrics.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !data ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-800 text-sm">
            Failed to load board overview. Please refresh.
          </div>
        ) : (
          <>
            {/* ── Financial Summary ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-primary" />
                Financial Summary
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  label="Total Donated"
                  value={fmt(data.financials.totalDonations)}
                  sub={`${data.financials.donationCount.toLocaleString()} donations`}
                  icon={TrendingUp}
                  accent="bg-primary"
                />
                <StatCard
                  label="Total Paid Out"
                  value={fmt(data.financials.totalPaidOut)}
                  sub={`${data.financials.payoutBatchCount} batches`}
                  icon={Send}
                  accent="bg-emerald-600"
                />
                <StatCard
                  label="Net Reserve"
                  value={fmt(data.financials.netReserve)}
                  sub="across all pools"
                  icon={DollarSign}
                  accent="bg-blue-600"
                />
                <StatCard
                  label="People Helped"
                  value={data.debtStats.paid.toLocaleString()}
                  sub={`${data.debtStats.queued} in queue`}
                  icon={CheckCircle2}
                  accent="bg-green-600"
                />
              </div>
            </section>

            {/* ── Program Stats ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                Program Stats
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-xl border p-4">
                  <p className="text-xs text-muted-foreground">Active Pools</p>
                  <p className="text-2xl font-bold mt-0.5">
                    {data.pools.filter((p) => p.status === "active").length}
                  </p>
                </div>
                <div className="rounded-xl border p-4">
                  <p className="text-xs text-muted-foreground">Total Pools</p>
                  <p className="text-2xl font-bold mt-0.5">{data.pools.length}</p>
                </div>
                <div className="rounded-xl border p-4">
                  <p className="text-xs text-muted-foreground">Total Applications</p>
                  <p className="text-2xl font-bold mt-0.5">{data.debtStats.total.toLocaleString()}</p>
                </div>
                <div className="rounded-xl border p-4">
                  <p className="text-xs text-muted-foreground">Pending Review</p>
                  <p className="text-2xl font-bold mt-0.5">{data.debtStats.pending.toLocaleString()}</p>
                </div>
              </div>
            </section>

            {/* ── Open Compliance Alerts ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-primary" />
                Open Compliance Alerts
                {data.compliance.openAlerts > 0 && (
                  <Badge variant="outline" className="ml-1 text-xs bg-red-50 text-red-700 border-red-200">
                    {data.compliance.openAlerts} open
                  </Badge>
                )}
              </h2>
              {data.compliance.openAlerts === 0 ? (
                <div className="rounded-xl border p-6 flex items-center gap-3 text-green-700 bg-green-50">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">No open compliance alerts</p>
                    <p className="text-xs text-green-600 mt-0.5">All compliance items are resolved.</p>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Type</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Severity</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Notes</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Detected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.compliance.items.map((alert) => (
                        <tr key={alert.id} className="border-t">
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded capitalize">
                              {alert.alertType.replace(/_/g, " ")}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${
                              alert.severity === "critical" ? "bg-red-100 text-red-800 border-red-200" :
                              alert.severity === "high" ? "bg-orange-100 text-orange-800 border-orange-200" :
                              alert.severity === "medium" ? "bg-yellow-100 text-yellow-800 border-yellow-200" :
                              "bg-gray-100 text-gray-700 border-gray-200"
                            }`}>
                              <AlertTriangle className="h-3 w-3" />
                              {alert.severity}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground hidden sm:table-cell max-w-xs truncate">
                            {alert.notes ?? "—"}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground text-right whitespace-nowrap">
                            {fmtDateTime(alert.detectedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {data.compliance.openAlerts > data.compliance.items.length && (
                    <div className="px-4 py-2.5 bg-muted/20 border-t text-xs text-muted-foreground text-center">
                      Showing {data.compliance.items.length} of {data.compliance.openAlerts} open alerts
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* ── Pool Health ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                Relief Pool Health
              </h2>
              <div className="rounded-xl border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Pool</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Balance</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Goal</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Funded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pools.map((pool) => (
                      <tr key={pool.id} className="border-t">
                        <td className="px-4 py-3 font-medium">{pool.name}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`text-xs capitalize ${POOL_STATUS_COLORS[pool.status] ?? ""}`}>
                            {pool.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{fmt(pool.balance)}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground font-mono text-sm">{fmt(pool.threshold)}</td>
                        <td className="px-4 py-3 text-right hidden sm:table-cell">
                          <div className="flex items-center gap-2 justify-end">
                            <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                              <div
                                className="h-full rounded-full bg-primary"
                                style={{ width: `${pool.percentFunded}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground w-8 text-right">{pool.percentFunded}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data.pools.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground text-sm">No pools found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ── Recent Payout Batches ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                Recent Payout Batches
              </h2>
              {data.recentBatches.length === 0 ? (
                <div className="rounded-xl border p-8 text-center text-sm text-muted-foreground">
                  No payout batches yet.
                </div>
              ) : (
                <div className="rounded-xl border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Batch</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Amount</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Debts</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentBatches.map((batch) => {
                        const cfg = BATCH_STATUS[batch.status] ?? BATCH_STATUS.sent;
                        return (
                          <tr key={batch.id} className="border-t">
                            <td className="px-4 py-3 text-muted-foreground">#{batch.id}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
                                {cfg.icon}
                                {cfg.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-mono font-medium">{fmt(batch.totalAmount)}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground hidden sm:table-cell">{batch.debtsCount}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground text-xs">{fmtDate(batch.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* ── Recent Audit Log ── */}
            <section>
              <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Recent Activity Log
              </h2>
              {data.recentAuditLogs.length === 0 ? (
                <div className="rounded-xl border p-8 text-center text-sm text-muted-foreground">
                  No activity recorded yet.
                </div>
              ) : (
                <div className="rounded-xl border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Action</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Actor</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentAuditLogs.map((log) => (
                        <tr key={log.id} className="border-t">
                          <td className="px-4 py-2.5">
                            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                              {log.action}
                            </span>
                            {log.entityType && log.entityId && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {log.entityType} #{log.entityId}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground hidden sm:table-cell">
                            {log.actorEmail ?? "—"}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground text-right whitespace-nowrap">
                            {fmtDateTime(log.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="text-center pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                DebtBridge 501(c)(3) · Board governance portal · Read-only view
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
